<template>
  <div>
    <fetching-data-spinner
      :error="error"
      :loading="loading"
      :loading-title="$t('subscription.loading-plans')"
      enable-retry
      @retry="loadSettings"
    />
    <slide-y-up-transition>
      <setup-wizard
        v-if="!loading && !error"
        ref="wizard"
        :navigation="false"
        :tabs="tabs"
        @complete="handleComplete"
        @skip="handleSkip"
      >
        <template
          #actions="{ next, prev, isLastStep, variant, isRTL, loading: loadingNext, nextButtonClass }"
        >
          <template v-if="isLastStep">
            <b-button
              v-ripple.400="'rgba(0, 0, 0, 0.15)'"
              variant="outline-dark"
              class="mr-1 action-button"
              size="lg"
              @click="prev"
            >
              <span class="d-none d-md-inline">
                {{ $t('forms.prev') }}
              </span>
              <feather-icon
                class="d-inline d-md-none"
                :icon="isRTL ? 'ChevronRightIcon' : 'ChevronLeftIcon'"
              />
            </b-button>

            <b-button
              v-ripple.400="'rgba(0, 0, 0, 0.15)'"
              :variant="variant"
              :disabled="loading"
              class="action-button next-button"
              :class="nextButtonClass"
              size="lg"
              @click="next"
            >
              <span>
                <template v-if="total === 0">
                  {{ $t('subscription.subscribe') }}
                </template>
                <template v-else>
                  {{ isUpgrade ? $t('subscription.upgrade') : $t('payment.pay') }}
                </template>
              </span>
              <b-spinner
                v-if="loadingNext"
                class="ml-1"
                small
                label="submitting..."
              />
            </b-button>
          </template>
        </template>
      </setup-wizard>
    </slide-y-up-transition>
  </div>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import { mapFields } from 'vuex-map-fields'
import Ripple from 'vue-ripple-directive'
import config from './purchase-wizard'
import SetupWizard from '@/@okmenu/components/wizards/setup-wizard/SetupWizard.vue'
import ErrorHandler from '@/utils/error-handler'
import FetchingDataSpinner from '@/@okmenu/components/utils/FetchingDataSpinner'
import MarketingService, { MARKETING_EVENTS } from '@/services/marketing-service'

export default {
  name: 'PurchaseWizard',
  components: {
    FetchingDataSpinner,
    SetupWizard,
    BButton,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  props: {
    addonSlug: {
      type: String,
      default: null,
    },
    addonSlugs: {
      type: Array,
      default: null,
    },
    planId: {
      type: Number,
      default: null,
    },
    addonLimit: {
      type: Number,
      default: 1,
    },
    cycle: {
      type: String,
      default: 'yearly',
    },
    upgradeModal: {
      type: Boolean,
      default: false,
    },
    skipButtonTitle: {
      type: String,
      default: null,
    },
    source: {
      type: String,
      default: 'addon',
      validations: v => ['addon', 'upgrade', 'invoice', 'plans'].includes(v),
    },
  },
  data() {
    return {
      loading: true,
      error: null,
    }
  },
  computed: {
    ...mapFields('purchase', ['features', 'plan']),
    ...mapGetters({
      addons: 'pricing/addons',
      packages: 'pricing/regularPackages',
      isTrial: 'subscription/isTrial',
      hideFirstStep: 'purchase/hideFirstStep',
      isUpgrade: 'purchase/isUpgrade',
      purchaseSucceed: 'purchase/purchaseSucceed',
      subscribeToFreeOnly: 'purchase/subscribeToFreeOnly',
      total: 'purchase/total',
    }),
    tabs() {
      const { tabs } = cloneDeep(config)

      if (this.hideFirstStep) {
        tabs[0].hide = true
        tabs[1].canSkip = true
      }

      return tabs.map(tab => ({ ...tab, skipButtonTitle: this.skipButtonTitle ? this.skipButtonTitle : null }))
    },
  },
  mounted() {
    this.loadSettings().then(() => {
      const {
        addonSlug, planId, cycle, addonSlugs, upgradeModal, source, addonLimit,
      } = this
      this.$store.dispatch('purchase/INIT_PURCHASE', {
        addonSlug, addonSlugs, planId, cycle, upgradeModal, source, addonLimit,
      })
    })
  },
  methods: {
    handleComplete() {
      this.$emit('complete', this.purchaseSucceed)
    },
    handleSkip() {
      MarketingService.triggerEvent(MARKETING_EVENTS.PURCHASE_FAILED, 'Purchase', 'user cancelled purchased')
      this.$emit('skip')
    },
    loadSettings() {
      this.loading = true
      this.error = null
      return Promise.all([this.$store.dispatch('pricing/FETCH_PRICING'), this.$store.dispatch('subscription/FETCH_SUBSCRIPTION_DETAILS')])
        .catch(e => {
          const { message } = ErrorHandler.getErrorDetails(e)
          this.error = message
        })
        .finally(() => { this.loading = false })
    },
  },
}
</script>
